import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheckCircle, FiCalendar } from "react-icons/fi";
import { settingsAPI } from "../services/api";

const fallbackPrograms = [
  {
    _id: "1",
    title: "Montessori / Pre-Primary",
    grades: "Play Group – UKG",
    age: "Age: ~2.5 – 5 years",
    subjects: ["Play Group (PG)", "Nursery", "Lower Kindergarten (LKG)", "Upper Kindergarten (UKG)"],
    description: "Our Pre-Primary program focuses on basic learning, play, and behaviour to build a strong early foundation for young minds.",
    highlights: ["Play-based learning environment", "Focus on basic behaviour and socialisation", "Interactive games and storytelling", "Caring and trained Montessori teachers"],
  },
  {
    _id: "2",
    title: "Primary Level",
    grades: "Class 1 – 5",
    age: "Age: ~6 – 10 years",
    subjects: ["Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Mathematics", "English", "Science"],
    description: "Dedicated to foundational knowledge and mastery of core subjects like Math, English, and Science.",
    highlights: ["Strong foundation in basic subjects", "Regular assessments and progress tracking", "Focus on reading and writing skills", "Extracurricular activities integration"],
  },
  {
    _id: "3",
    title: "Lower Secondary Level",
    grades: "Class 6 – 8",
    age: "Age: ~11 – 13 years",
    subjects: ["Class 6", "Class 7", "Class 8", "Advanced Math", "General Science", "Social Studies"],
    description: "Designed for concept building, critical thinking, and introduction to more advanced subjects bridging primary and high school.",
    highlights: ["Concept building methodologies", "Introduction to advanced subjects", "Critical thinking and problem solving", "Project-based learning"],
  },
  {
    _id: "4",
    title: "Secondary Level",
    grades: "Class 9 – 10",
    age: "Age: ~14 – 16 years",
    subjects: ["Class 9", "Class 10", "Compulsory Math", "Optional Math", "Science", "English"],
    description: "Rigorous academic preparation focusing on the SEE (School Leaving Certificate) under the National Examination Board.",
    highlights: ["SEE Board Exam preparation", "Comprehensive syllabus coverage", "Mock tests and revision classes", "Career counseling for higher education"],
  },
];

const fallbackCalendar = [
  { month: "April",    event: "New Academic Session Begins" },
  { month: "May",      event: "First Unit Test" },
  { month: "July",     event: "Mid-Term Examinations" },
  { month: "August",   event: "Sports Day & Cultural Programs" },
  { month: "October",  event: "Second Unit Test" },
  { month: "December", event: "Annual Examinations" },
  { month: "February", event: "Board Examinations (Grade 10)" },
  { month: "March",    event: "Academic Year Ends / Results" },
];

const accentColors = ["bg-[#2563EB]", "bg-[#1e3a8a]", "bg-[#0EA5E9]", "bg-[#1e40af]"];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

export default function Academics() {
  const [programs, setPrograms] = useState(fallbackPrograms);
  const [calendarEvents, setCalendarEvents] = useState(fallbackCalendar);
  const location = useLocation();

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const s = res.data?.data?.settings;
      if (s?.programs?.length > 0) setPrograms(s.programs);
      if (s?.academicCalendar?.length > 0) setCalendarEvents(s.academicCalendar);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.getElementById(location.hash.substring(1));
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Education</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Academic <span className="text-sky-300">Programs</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Comprehensive academic programs for every stage of your child's educational journey.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Programs */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 divide-y divide-blue-50">
          {programs.map((program, idx) => (
            <motion.div
              key={program._id || idx}
              id={program._id || String(idx)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
            >
              {/* Left */}
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-11 h-11 ${accentColors[idx % accentColors.length]} rounded-xl flex items-center justify-center shadow-sm`}>
                    <span className="text-white font-bold text-xs">0{idx + 1}</span>
                  </div>
                  <div>
                    {program.grades && <span className="text-[#2563EB] text-xs font-bold uppercase tracking-widest">{program.grades}</span>}
                    {program.age && <span className="text-slate-400 text-xs ml-3">{program.age}</span>}
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] leading-tight mb-4">{program.title}</h2>
                <p className="text-slate-500 text-sm leading-relaxed mb-8">{program.description}</p>
                {program.highlights?.length > 0 && (
                  <div className="space-y-3">
                    {program.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <FiCheckCircle className="w-4 h-4 text-[#2563EB] shrink-0" />
                        <span className="text-slate-600 text-sm">{h}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right — subjects */}
              {program.subjects?.length > 0 && (
                <div className="bg-[#F0F4FF] rounded-2xl p-8 border border-blue-100">
                  <p className="text-xs font-bold uppercase tracking-widest text-[#2563EB] mb-6">Subjects Offered</p>
                  <div className="grid grid-cols-2 gap-3">
                    {program.subjects.map((subject, i) => (
                      <div key={i} className="bg-white rounded-xl px-4 py-3 text-slate-700 text-sm font-medium border border-blue-100 shadow-sm">
                        {subject}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Academic Calendar */}
      <section id="calendar" className="py-20 lg:py-28 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <span className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Planning Ahead</span>
            </div>
            <h2 className="text-4xl font-bold text-white">Academic <span className="text-sky-300">Calendar</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {calendarEvents.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                viewport={{ once: true }}
                className="bg-white/10 rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
                    <FiCalendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sky-300 text-xs font-bold uppercase tracking-widest">{item.month}</span>
                </div>
                <p className="text-blue-100/80 text-sm leading-relaxed">{item.event}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
